import { createElement } from "react"
import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { badgeIcon } from "@/lib/badge-icons"
import type { NextStreakBadge } from "@/types/streak"

/**
 * OKS-515 — §12 badge-progress card.
 *
 * Shows the next unearned STREAK badge (the catalogue's `perfect_streak_5`,
 * PRD Q3 — Figma's "Trailblazer · 14d" is illustrative only). Medallion +
 * "N days to your {badge} badge" + progress bar + "X / Y days".
 *
 * The parent only renders this when `next_streak_badge` is non-null (it's
 * null once the badge is earned), so this component assumes a present badge.
 * Decorative glyph is `aria-hidden`; the copy carries the meaning.
 */
interface StreakBadgeProgressProps {
  badge: NextStreakBadge
}

export function StreakBadgeProgress({ badge }: StreakBadgeProgressProps) {
  const { t } = useTranslation()

  const badgeName = t(`badges.milestones.${badge.i18n_key}.name`)
  const percent =
    badge.target_days > 0
      ? Math.round((badge.current_days / badge.target_days) * 100)
      : 0

  return (
    <Card data-testid="streak-badge-progress">
      <CardContent className="flex items-center gap-4 py-4">
        <span
          aria-hidden="true"
          className="flex size-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white"
        >
          {createElement(badgeIcon(badge.icon), { className: "size-7" })}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="text-sm font-semibold">
            {t("streak.detail.next_badge.title", {
              count: badge.remaining_days,
              badge: badgeName,
            })}
          </p>
          <Progress
            value={percent}
            className="bg-muted [&>[data-slot=progress-indicator]]:bg-gradient-to-r [&>[data-slot=progress-indicator]]:from-amber-400 [&>[data-slot=progress-indicator]]:to-orange-500"
          />
          <p className="text-muted-foreground text-xs">
            {t("streak.detail.next_badge.progress", {
              current: badge.current_days,
              target: badge.target_days,
            })}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
